import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import capitalizeFirst from '@zola-helpers/client/dist/es/transformers/capitalizeFirst';
import { ButtonColorway, ButtonSize, ButtonVariant } from './types';
import { ButtonWrapper, ButtonComponent } from './ButtonV3.styles';

export type ButtonV3Props = Partial<ButtonHTMLAttributes<HTMLButtonElement>> &
  Partial<AnchorHTMLAttributes<HTMLAnchorElement>> & {
    component?: ButtonComponent;
    variant?: ButtonVariant;
    /** Preapproved button colorways. Only available for primary and secondary variants. Only to be used on backgrounds matching the colorway. */
    colorway?: ButtonColorway;
    size?: ButtonSize;
    bordered?: boolean;
    fullWidth?: boolean;
    textTransform?: boolean;
    className?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    innerRef?: React.RefObject<any>;
    /** @deprecated Use size='small' instead */
    compact?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  };

const ButtonV3: React.FC<ButtonV3Props> = ({
  children,
  disabled,
  component: Component = 'button',
  variant = 'primary',
  bordered,
  compact,
  fullWidth,
  textTransform = true,
  className,
  onClick,
  rel,
  role = 'button',
  tabIndex,
  type = 'button',
  href,
  innerRef,
  target,
  colorway = 'black',
  size = 'medium',
  ...props
}) => {
  const elementProps = {
    onClick: !disabled && onClick ? onClick : undefined,
    className,
    href: Component === 'a' ? href : undefined,
    tabIndex: Component === 'button' ? tabIndex : undefined,
    variant,
    disabled,
    bordered,
    fullWidth,
    compact,
    type,
    role,
    target,
    rel,
    colorway,
    size,
    ...props,
  };

  if (disabled) elementProps.disabled = disabled;
  if (Component === 'button') {
    elementProps.type = type;
    elementProps.role = role;
    if (tabIndex === undefined && disabled) {
      elementProps.tabIndex = -1;
    }
  } else if (Component === 'a') {
    // @ts-expect-error links do not have this property
    delete elementProps.type;
    // @ts-expect-error links do not have this property
    delete elementProps.role;
    if (target !== '') {
      elementProps.target = target;
      if (target === '_blank') {
        elementProps.rel = `noopener noreferrer ${rel}`;
      }
    }
    if (!href) {
      elementProps.href = '#';
    }
  }
  if (compact) elementProps.size = 'small';

  const applySentenceCase = textTransform && typeof children === 'string';

  return (
    <ButtonWrapper as={Component} ref={innerRef} {...elementProps}>
      {applySentenceCase ? capitalizeFirst(String(children || ''), 1) : children}
    </ButtonWrapper>
  );
};

export default ButtonV3;
